import clsx from "clsx";
import { CSSProperties, ReactNode } from "react";

import { Button } from "@/components/atoms/buttons/Button";
import { CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import { getCorrectUrl } from "@/utils/common";
import { contentToHtml } from "@/utils/draft";

import CornerIcon from "/public/icon/lp-parts/corner.svg";

interface Props {
  part: CtaButtonTypeTemplate;
  formPath: string | undefined;
  onClick?: () => void;
  disabled?: boolean;
}

// part_lp 8
export default function CtaButton2Content({
  part,
  formPath,
  onClick,
  disabled,
}: Props) {
  const buttonHref = (): string => {
    if (part.buttonContent.link?.url) {
      return getCorrectUrl(part.buttonContent.link?.url);
    }
    if (formPath) {
      return formPath;
    }
    return "";
  };

  const isNewTab = !!(
    part.buttonContent.link?.isNewTab || part.buttonContent.service?.isNewTab
  );

  function ButtonRenderer({
    children,
    styles,
    disabled,
  }: {
    children: ReactNode;
    styles?: CSSProperties;
    disabled?: boolean;
  }) {
    const baseClassNames =
      "relative block text-white font-bold cursor-pointer disabled:pointer-events-none disabled:cursor-default";
    const largeDoubleArrow = [
      baseClassNames,
      "box-border !py-[13px] !px-8 !mx-auto !w-full min-h-[64px] rounded border-2 border-solid disabled:opacity-30 transition md:min-h-[72px] h-auto",
    ].join(" ");
    const linkRef = buttonHref();

    if (disabled) {
      return (
        <div className={largeDoubleArrow + " opacity-50"} style={styles}>
          {children}
        </div>
      );
    }

    return (
      <Button
        className={largeDoubleArrow}
        style={styles}
        href={linkRef}
        onClick={onClick}
        label={children}
        target={isNewTab ? "_blank" : undefined}
      ></Button>
    );
  }

  return (
    <div className="relative p-[14px]">
      <div className="absolute top-0 left-0">
        <div className="rotate-0">
          <CornerIcon
            stroke={part.decorationCssProperties?.backgroundColor}
            opacity={part.decorationCssProperties?.opacity}
          />
        </div>
      </div>
      <div className="absolute top-0 right-0">
        <div className="rotate-90">
          <CornerIcon
            stroke={part.decorationCssProperties?.backgroundColor}
            opacity={part.decorationCssProperties?.opacity}
          />
        </div>
      </div>
      <div className="absolute right-0 bottom-0">
        <div className="rotate-180">
          <CornerIcon
            stroke={part.decorationCssProperties?.backgroundColor}
            opacity={part.decorationCssProperties?.opacity}
          />
        </div>
      </div>
      <div className="absolute bottom-0 left-0">
        <div className="-rotate-90">
          <CornerIcon
            stroke={part.decorationCssProperties?.backgroundColor}
            opacity={part.decorationCssProperties?.opacity}
          />
        </div>
      </div>

      <div
        className="flex flex-col gap-6 py-[48px] px-[16px] md:px-0"
        style={part.frameCssProperties}
      >
        {part.description && part.description.content && (
          <div className="m-auto w-full">
            <div
              dangerouslySetInnerHTML={{
                __html: contentToHtml(part.description.content),
              }}
              className={clsx(
                "flex flex-col items-center min-h-[1.5em] text-center break-all",
                "text-2xl font-bold leading-normal md:text-[32px]",
              )}
              style={part.description.textBaseStyle}
            ></div>
          </div>
        )}

        {part.buttonContent.content && (
          <div className="m-auto w-full md:max-w-[400px]">
            <ButtonRenderer
              styles={part.buttonContent.cssProperties}
              disabled={disabled}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: contentToHtml(part.buttonContent.content),
                }}
                className="flex flex-col min-h-[1.5em] text-xl font-bold leading-normal text-center md:text-2xl"
                style={part.buttonContent.textBaseStyle}
              />
            </ButtonRenderer>
          </div>
        )}
      </div>
    </div>
  );
}
